import mawiDB from './indexedDBService';

// Hardware detection and WebLLM backend configuration
interface HardwareInfo {
  hasGPU: boolean;
  gpuVendor: string | null;
  gpuRenderer: string | null;
  isMobile: boolean;
  hasWebGPU: boolean;
  hasWebGL: boolean;
  recommendedBackend: 'webgpu' | 'webgl' | 'wasm';
  hasNvidiaGPU: boolean;
  hasOllama: boolean;
}

class HardwareDetector {
  private hardwareInfo: HardwareInfo | null = null;

  async detectHardware(): Promise<HardwareInfo> {
    if (this.hardwareInfo) return this.hardwareInfo;

    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    let gpuVendor = null;
    let gpuRenderer = null;
    let hasWebGPU = false;
    let hasWebGL = false;

    // Check WebGL support
    if (gl) {
      hasWebGL = true;
      const webglContext = gl as WebGLRenderingContext;
      const debugInfo = webglContext.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        gpuVendor = webglContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        gpuRenderer = webglContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }

    // Check WebGPU support (Chrome 113+, Edge 113+)
    if ('gpu' in navigator) {
      try {
        // powerPreference is ignored on Windows by Chromium, will show warning
        // Pass without explicit powerPreference to suppress warning
        const adapter = await (navigator as any).gpu.requestAdapter({});
        hasWebGPU = !!adapter;
      } catch (e) {
        hasWebGPU = false;
      }
    }

    // Determine if it's a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Check for NVIDIA GPU
    const hasNvidiaGPU = gpuVendor?.toLowerCase().includes('nvidia') ||
                        gpuRenderer?.toLowerCase().includes('nvidia') ||
                        gpuRenderer?.toLowerCase().includes('geforce') ||
                        gpuRenderer?.toLowerCase().includes('rtx') ||
                        gpuRenderer?.toLowerCase().includes('gtx') ||
                        gpuRenderer?.toLowerCase().includes('quadro') ||
                        gpuRenderer?.toLowerCase().includes('tesla');

    // Skip automatic Ollama detection to avoid CSP violations
    const hasOllama = false;

    // Determine recommended backend
    let recommendedBackend: 'webgpu' | 'webgl' | 'wasm' = 'wasm';

    if (hasWebGPU && !isMobile) {
      recommendedBackend = 'webgpu';
    } else if (hasWebGL) {
      recommendedBackend = 'webgl';
    }

    this.hardwareInfo = {
      hasGPU: hasWebGPU || hasWebGL,
      gpuVendor,
      gpuRenderer,
      isMobile,
      hasWebGPU,
      hasWebGL,
      hasNvidiaGPU,
      hasOllama,
      recommendedBackend
    };

    console.log('Hardware detection results:', this.hardwareInfo);
    return this.hardwareInfo;
  }

  // Check if Ollama is available (for potential fallback)
  private async checkOllamaAvailability(): Promise<boolean> {
    try {
      // Try to connect to Ollama API
      const response = await fetch('http://localhost:11434/api/tags', {
        method: 'GET',
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
      return response.ok;
    } catch (error) {
      console.log('Ollama not available:', error);
      return false;
    }
  }

  // Get WebLLM backend configuration based on hardware
  getWebLlmConfig() {
    if (!this.hardwareInfo) return {};

    const config: any = {};

  // Always use IndexedDB for maximum performance and permanent model storage
  config.cacheBackend = "indexeddb";

    return config;
  }

  // Get recommended models based on hardware capabilities
  getRecommendedModels(hardwareInfo: HardwareInfo, allModels: ModelMetadata[]): ModelMetadata[] {
    // Detect CPU core count for fine-grained CPU tiering
    const cpuCores = navigator.hardwareConcurrency || 4;
    
    // Filter and prioritize models based on hardware
    if (hardwareInfo.hasNvidiaGPU || hardwareInfo.hasOllama) {
      // NVIDIA GPU or Ollama available - prefer larger models
      return allModels.filter((model: ModelMetadata) =>
        ['qwen2.5-7b', 'phi3.5-3.8b', 'qwen2.5-3b'].includes(model.id)
      );
    } else if (hardwareInfo.hasGPU && hardwareInfo.recommendedBackend === 'webgpu') {
      // Good GPU with WebGPU support
      return allModels.filter((model: ModelMetadata) =>
        ['phi3.5-3.8b', 'qwen2.5-3b', 'qwen2-1.5b'].includes(model.id)
      );
    } else if (cpuCores >= 8 && !hardwareInfo.isMobile) {
      // Modern CPU with 8+ cores
      return allModels.filter((model: ModelMetadata) =>
        ['qwen2.5-3b', 'qwen2-1.5b', 'smollm2-1.7b'].includes(model.id)
      );
    } else if (cpuCores >= 4) {
      // Mid range CPU 4-8 cores
      return allModels.filter((model: ModelMetadata) =>
        ['qwen2-1.5b', 'smollm2-1.7b', 'tinyllama-1.1b'].includes(model.id)
      );
    } else {
      // Low end CPU / Mobile
      return allModels.filter((model: ModelMetadata) =>
        ['tinyllama-1.1b', 'smollm2-1.7b'].includes(model.id)
      );
    }
  }
}
const hardwareDetector = new HardwareDetector();

interface ModelMetadata {
  id: string;
  name: string;
  displayName: string;
  type: 'local' | 'cloud';
  path?: string; // For local models
  downloadedAt?: string;
  size?: number;
}

// Model cache entry for tracking loaded models
interface ModelCacheEntry {
  modelId: string;
  modelUrl: string;
  loadedAt: number;
  lastUsed: number;
  loadTime: number;
  memoryUsage?: number;
  performance: {
    averageResponseTime: number;
    totalRequests: number;
    successRate: number;
  };
}

class ModelManager {
  private availableModels: ModelMetadata[] = [
    { id: 'tinyllama-1.1b', name: 'TinyLlama-1.1B-Chat-v1.0-q4f16_1-MLC', displayName: 'TinyLlama 1.1B', type: 'local', size: 713000000 },
    { id: 'gemma2-2b', name: 'gemma-2b-it-q4f16_1-MLC', displayName: 'Gemma 2B', type: 'local', size: 1250000000 },
    { id: 'smollm2-1.7b', name: 'SmolLM2-1.7B-Instruct-q4f16_1-MLC', displayName: 'SmolLM2 1.7B', type: 'local', size: 1040000000 },
    { id: 'qwen2-1.5b', name: 'Qwen2-1.5B-Instruct-q4f32_1-MLC', displayName: 'Qwen2 1.5B', type: 'local', size: 1150000000 },
    { id: 'phi3.5-3.8b', name: 'Phi-3.5-mini-instruct-q4f16_1-MLC', displayName: 'Phi 3.5 Mini', type: 'local', size: 2300000000 },
    { id: 'qwen2.5-3b', name: 'Qwen2.5-3B-Instruct-q4f32_1-MLC', displayName: 'Qwen2.5 3B', type: 'local', size: 2510000000 },
    { id: 'qwen2.5-7b', name: 'Qwen2.5-7B-Instruct-q4f32_1-MLC', displayName: 'Qwen2.5 7B', type: 'local', size: 5320000000 },
  ];

  // Multi-model caching system
  private modelCache: Map<string, ModelCacheEntry> = new Map();
  private maxCachedModels = 3; // Limit concurrent loaded models
  private cacheExpirationTime = 30 * 60 * 1000; // 30 minutes
  private initialized = false;

  /**
   * Initialize the model manager (load cache metadata and ensure models stored)
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    // Initialize IndexedDB first
    await mawiDB.initialize();

    // Ensure static models are stored in IndexedDB
    await this.ensureStaticModelsStored();

    // Load cache metadata
    await this.loadCacheMetadata();

    this.initialized = true;
    console.log('ModelManager initialized with models and cache metadata');
  }

  /**
   * Force reinitialization - useful for fixing database issues
   */
  async forceReinitialize(): Promise<void> {
    console.log('Force reinitializing ModelManager...');
    this.initialized = false;
    await this.initialize();
  }

  /**
   * Ensure static models are stored in IndexedDB
   */
  private async ensureStaticModelsStored(): Promise<void> {
    try {
      console.log('Ensuring static models are stored in IndexedDB...');

      // Only initialize database if completely empty
      const existingModels = await mawiDB.llm.getModels();
      
      if (existingModels.length === 0) {
        console.log('No existing models found - initializing clean model database');
        await this.initializeCleanModelDatabase();
      } else {
        console.log(`Found ${existingModels.length} existing models, skipping reinitialization`);
        
        // Verify all static models exist, add any missing ones
        for (const model of this.availableModels) {
          const exists = existingModels.find(m => m.modelId === model.id);
          if (!exists) {
            console.log(`Adding missing model: ${model.id}`);
            await mawiDB.llm.createModel({
              name: model.name,
              provider: model.type === 'local' ? 'webllm' : 'unknown',
              modelId: model.id,
              type: model.type,
              size: model.size,
              path: model.path,
              downloadedAt: model.downloadedAt,
              isDownloaded: !!model.downloadedAt,
            });
          }
        }
      }

      console.log('Static models metadata storage check completed');
    } catch (error) {
      console.error('Failed to ensure static models metadata are stored:', error);
    }
  }

  private async initializeCleanModelDatabase(): Promise<void> {
    try {
      console.log('Initializing clean model database...');

      // Get all existing models
      const existingModels = await mawiDB.llm.getModels();
      console.log(`Found ${existingModels.length} existing models in database`);

      // Clear all existing models to prevent duplicates
      for (const model of existingModels) {
        try {
          await mawiDB.llm.deleteModel(model.id);
        } catch (error) {
          console.warn(`Failed to delete model ${model.id}:`, error);
        }
      }

      // Store fresh static models
      for (const model of this.availableModels) {
        try {
          await mawiDB.llm.createModel({
            name: model.name,
            provider: model.type === 'local' ? 'webllm' : 'unknown',
            modelId: model.id,
            type: model.type,
            size: model.size,
            path: model.path,
            downloadedAt: model.downloadedAt,
            isDownloaded: !!model.downloadedAt,
          });
          console.log(`Stored model metadata: ${model.id}`);
        } catch (error) {
          console.error(`Failed to store model metadata ${model.id}:`, error);
        }
      }

      console.log(`Successfully initialized ${this.availableModels.length} static models`);
    } catch (error) {
      console.error('Failed to initialize clean model database:', error);
      throw error;
    }
  }

  /**
   * Get all available models with cache status
   */
  async getAvailableModels(): Promise<ModelMetadata[]> {
    // Ensure initialization
    await this.initialize();

    // For now, just return the static models directly
    // The stored models are used for download status and performance data
    // but the main model list should be the static definitions
    console.log(`Returning ${this.availableModels.length} static models:`, this.availableModels.map(m => ({ id: m.id, name: m.name })));
    return this.availableModels;
  }

  /**
   * Get all available models with enhanced metadata
   */
  async getAvailableModelsWithMetadata(): Promise<Array<ModelMetadata & {
    isCached: boolean;
    performance?: ModelCacheEntry['performance'];
    cacheEntry?: ModelCacheEntry;
  }>> {
    const models = await this.getAvailableModels();

    return models.map(model => ({
      ...model,
      isCached: this.isModelCached(model.id),
      performance: this.getModelPerformance(model.id) || undefined,
      cacheEntry: this.getCachedModel(model.id) || undefined
    }));
  }

  /**
   * Download and cache a model
   */
  async downloadModel(modelId: string): Promise<void> {
    try {
      // Model loading is handled by WebLlmProvider
      // Here we update the model record to mark it as downloaded
      const existing = await mawiDB.llm.getModel(modelId);
      if (existing) {
        await mawiDB.llm.updateModel(modelId, {
          isDownloaded: true,
          downloadedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error(`Failed to update model metadata ${modelId}:`, error);
      throw error;
    }
  }

  /**
   * Switch to a model
   */
  async switchModel(modelId: string): Promise<void> {
    const model = await this.getModelMetadata(modelId);
    if (!model) {
      throw new Error(`Model ${modelId} not found`);
    }

    // For WebLLM, we just return the model info
    // The actual switching is handled by WebLlmProvider.setModelUrl
    return Promise.resolve();
  }

  getModelUrl(modelId: string): string | null {
    // For @mlc-ai/web-llm, use proper model identifiers
    // Updated working identifiers as of May 2026
    const modelIdentifiers: { [key: string]: string } = {
      'tinyllama-1.1b': 'TinyLlama-1.1B-Chat-v1.0-q4f16_1-MLC',
      'gemma2-2b': 'gemma-2b-it-q4f16_1-MLC',
      'smollm2-1.7b': 'SmolLM2-1.7B-Instruct-q4f16_1-MLC',
      'qwen2-1.5b': 'Qwen2-1.5B-Instruct-q4f32_1-MLC',
      'phi3.5-3.8b': 'Phi-3.5-mini-instruct-q4f16_1-MLC',
      'qwen2.5-3b': 'Qwen2.5-3B-Instruct-q4f32_1-MLC',
      'qwen2.5-7b': 'Qwen2.5-7B-Instruct-q4f32_1-MLC',
    };
    return modelIdentifiers[modelId] || null;
  }

  /**
   * Get current model
   */
  getCurrentModel(): string | null {
    // Current model is managed by WebLlmProvider
    return null;
  }

  /**
   * Get model metadata
   */
  async getModelMetadata(modelId: string): Promise<ModelMetadata | null> {
    const stored = await mawiDB.llm.getModel(modelId);
    if (stored) {
      return {
        id: stored.id,
        name: stored.name,
        displayName: stored.name,
        type: stored.type,
        path: stored.path,
        downloadedAt: stored.downloadedAt,
        size: stored.size,
      };
    }

    return this.availableModels.find(m => m.id === modelId) || null;
  }

  /**
   * Get stored models from IndexedDB
   */
  private async getStoredModels(): Promise<ModelMetadata[]> {
    try {
      const models = await mawiDB.llm.getModels();
      console.log(`Loaded ${models.length} model metadata from LLM database`);
      // Map LLMModel back to ModelMetadata format
      return models.map(model => ({
        id: model.id,
        name: model.name,
        displayName: model.name,
        type: model.type,
        path: model.path,
        downloadedAt: model.downloadedAt,
        size: model.size,
      }));
    } catch (error) {
      console.error('Failed to get stored models:', error);
      return [];
    }
  }

  /**
   * Delete a downloaded model
   */
  async deleteModel(modelId: string): Promise<void> {
    // Remove from cache if present
    this.modelCache.delete(modelId);
    // Model unloading is handled by WebLlmProvider
    await mawiDB.llm.deleteModel(modelId);
  }

  /**
   * Clear all models from database and reinitialize with static models only
   * This method is used to fix duplication issues
   */
  async resetModelsDatabase(): Promise<void> {
    try {
      console.log('Resetting models database...');

      // Clear all models from database
      const existingModels = await mawiDB.llm.getModels();
      console.log(`Clearing ${existingModels.length} existing models...`);

      for (const model of existingModels) {
        try {
          await mawiDB.llm.deleteModel(model.id);
        } catch (error) {
          console.warn(`Failed to delete model ${model.id}:`, error);
        }
      }

      // Reset initialization flag to force re-initialization
      this.initialized = false;

      // Re-initialize to store fresh static models
      await this.initialize();

      console.log('Models database reset completed');
    } catch (error) {
      console.error('Failed to reset models database:', error);
      throw error;
    }
  }

  /**
   * Load model into cache
   */
  async loadModelToCache(modelId: string): Promise<ModelCacheEntry | null> {
    const modelUrl = this.getModelUrl(modelId);
    if (!modelUrl) return null;

    const startTime = Date.now();

    try {
      // Check if already cached and not expired
      const existing = this.modelCache.get(modelId);
      if (existing && (Date.now() - existing.loadedAt) < this.cacheExpirationTime) {
        existing.lastUsed = Date.now();
        return existing;
      }

      // Evict least recently used models if cache is full
      if (this.modelCache.size >= this.maxCachedModels) {
        this.evictLeastRecentlyUsed();
      }

      // Create new cache entry
      const cacheEntry: ModelCacheEntry = {
        modelId,
        modelUrl,
        loadedAt: Date.now(),
        lastUsed: Date.now(),
        loadTime: 0, // Will be set after successful load
        performance: {
          averageResponseTime: 0,
          totalRequests: 0,
          successRate: 1.0
        }
      };

      // Store in cache
      this.modelCache.set(modelId, cacheEntry);

      // Persist cache metadata
      await this.persistCacheMetadata();

      const loadTime = Date.now() - startTime;
      cacheEntry.loadTime = loadTime;

      console.log(`Model ${modelId} loaded to cache in ${loadTime}ms`);
      return cacheEntry;

    } catch (error) {
      console.error(`Failed to load model ${modelId} to cache:`, error);
      return null;
    }
  }

  /**
   * Get cached model entry
   */
  getCachedModel(modelId: string): ModelCacheEntry | null {
    const entry = this.modelCache.get(modelId);
    if (entry) {
      entry.lastUsed = Date.now();
      return entry;
    }
    return null;
  }

  /**
   * Check if model is cached and valid
   */
  isModelCached(modelId: string): boolean {
    const entry = this.modelCache.get(modelId);
    if (!entry) return false;

    // Check if expired
    if ((Date.now() - entry.loadedAt) > this.cacheExpirationTime) {
      this.modelCache.delete(modelId);
      return false;
    }

    return true;
  }

  /**
   * Get all cached models
   */
  getCachedModels(): ModelCacheEntry[] {
    return Array.from(this.modelCache.values());
  }

  /**
   * Evict least recently used model
   */
  private evictLeastRecentlyUsed(): void {
    let oldestEntry: ModelCacheEntry | null = null;
    let oldestKey = '';

    for (const [key, entry] of this.modelCache) {
      if (!oldestEntry || entry.lastUsed < oldestEntry.lastUsed) {
        oldestEntry = entry;
        oldestKey = key;
      }
    }

    if (oldestEntry) {
      console.log(`Evicting cached model: ${oldestEntry.modelId}`);
      this.modelCache.delete(oldestKey);
    }
  }

  /**
   * Persist cache metadata to IndexedDB
   */
  private async persistCacheMetadata(): Promise<void> {
    try {
      const cacheData = Array.from(this.modelCache.entries());
      // Cache metadata is now stored in memory only
      // In future versions, we might store this in the LLM database
      console.log(`Persisted ${cacheData.length} cache entries (in-memory only)`);
    } catch (error) {
      console.error('Failed to persist cache metadata:', error);
    }
  }

  /**
   * Load cache metadata from IndexedDB
   */
  private async loadCacheMetadata(): Promise<void> {
    // Cache metadata is now stored in memory only
    // In future versions, we might load this from the LLM database
    console.log('Cache metadata loading skipped (in-memory only)');
  }

  /**
   * Record model performance metrics
   */
  recordModelPerformance(modelId: string, responseTime: number, success: boolean): void {
    const entry = this.modelCache.get(modelId);
    if (!entry) return;

    entry.performance.totalRequests++;
    if (success) {
      // Update rolling average response time
      const currentAvg = entry.performance.averageResponseTime;
      const totalRequests = entry.performance.totalRequests;
      entry.performance.averageResponseTime = (currentAvg * (totalRequests - 1) + responseTime) / totalRequests;

      // Update success rate
      entry.performance.successRate = (entry.performance.successRate * (totalRequests - 1) + 1) / totalRequests;
    } else {
      entry.performance.successRate = (entry.performance.successRate * (entry.performance.totalRequests - 1)) / entry.performance.totalRequests;
    }

    // Persist updated metadata
    this.persistCacheMetadata();
  }

  /**
   * Get model performance statistics
   */
  getModelPerformance(modelId: string): ModelCacheEntry['performance'] | null {
    const entry = this.modelCache.get(modelId);
    return entry ? entry.performance : null;
  }

  /**
   * Smart model recommendation based on task and performance
   */
  async recommendModelForTask(taskType: 'chat' | 'code' | 'creative' | 'factual' | 'fast'): Promise<ModelMetadata | null> {
    const hardwareInfo = await hardwareDetector.detectHardware();
    const availableModels = await this.getAvailableModels();

    // Get performance data for all cached models
    const modelsWithPerformance = availableModels.map(model => ({
      ...model,
      performance: this.getModelPerformance(model.id),
      isCached: this.isModelCached(model.id)
    }));

    let recommendedModels: ModelMetadata[];

    // Task-specific model selection
    switch (taskType) {
      case 'code':
        // Prefer models good at code generation
        recommendedModels = modelsWithPerformance
          .filter(m => ['qwen2.5-7b', 'llama3-8b', 'mistral-7b'].includes(m.id))
          .map(m => m as ModelMetadata);
        break;

      case 'creative':
        // Prefer creative models with higher temperature compatibility
        recommendedModels = modelsWithPerformance
          .filter(m => ['llama3-8b', 'qwen2.5-7b', 'mistral-7b'].includes(m.id))
          .map(m => m as ModelMetadata);
        break;

      case 'factual':
        // Prefer accurate, smaller models for factual responses
        recommendedModels = modelsWithPerformance
          .filter(m => ['qwen2-1.5b', 'llama3-3b'].includes(m.id))
          .map(m => m as ModelMetadata);
        break;

      case 'fast':
        // Prefer smallest/fastest models
        recommendedModels = modelsWithPerformance
          .filter(m => ['llama3-1b', 'qwen2-1.5b'].includes(m.id))
          .map(m => m as ModelMetadata);
        break;

      case 'chat':
      default:
        // Use hardware-based recommendations
        recommendedModels = hardwareDetector.getRecommendedModels(hardwareInfo, availableModels);
        break;
    }

    if (recommendedModels.length === 0) return null;

    // Prioritize cached models, then by performance
    const sorted = recommendedModels
      .filter(model => modelsWithPerformance.find(m => m.id === model.id)?.isCached)
      .sort((a, b) => {
        const perfA = modelsWithPerformance.find(m => m.id === a.id)?.performance;
        const perfB = modelsWithPerformance.find(m => m.id === b.id)?.performance;

        if (!perfA && !perfB) return 0;
        if (!perfA) return 1;
        if (!perfB) return -1;

        // Prefer higher success rate, then faster response time
        const scoreA = perfA.successRate * 1000 - perfA.averageResponseTime;
        const scoreB = perfB.successRate * 1000 - perfB.averageResponseTime;
        return scoreB - scoreA;
      });

    return sorted[0] || recommendedModels[0];
  }

  /**
   * Preload popular models in background
   */
  async preloadPopularModels(): Promise<void> {
    const hardwareInfo = await hardwareDetector.detectHardware();
    const recommendedModels = hardwareDetector.getRecommendedModels(hardwareInfo, await this.getAvailableModels());

    // Preload top 2 recommended models
    const preloadPromises = recommendedModels.slice(0, 2).map(model =>
      this.loadModelToCache(model.id)
    );

    try {
      await Promise.all(preloadPromises);
      console.log('Popular models preloaded successfully');
    } catch (error) {
      console.error('Failed to preload some models:', error);
    }
  }

  /**
   * Get recommended models based on hardware
   */
  async getRecommendedModelsForHardware(): Promise<ModelMetadata[]> {
    const hardwareInfo = await hardwareDetector.detectHardware();
    const allModels = await this.getAvailableModels();

    return hardwareDetector.getRecommendedModels(hardwareInfo, allModels);
  }

  /**
   * Get performance estimate for a model on current hardware
   */
  async getModelPerformanceEstimate(modelId: string): Promise<{
    estimatedLoadTime: number;
    recommended: boolean;
    performance: 'fast' | 'medium' | 'slow';
  }> {
    const hardwareInfo = await hardwareDetector.detectHardware();

    const estimates = {
      'llama3-1b': { fast: 30, medium: 60, slow: 120 },
      'qwen2-1.5b': { fast: 45, medium: 90, slow: 180 },
      'llama3-3b': { fast: 90, medium: 180, slow: 300 },
      'llama3-8b': { fast: 180, medium: 360, slow: 600 },
      'qwen2.5-7b': { fast: 150, medium: 300, slow: 500 },
      'mistral-7b': { fast: 150, medium: 300, slow: 500 }
    };

    const modelEstimate = estimates[modelId as keyof typeof estimates];
    if (!modelEstimate) {
      return { estimatedLoadTime: 300, recommended: false, performance: 'slow' };
    }

    let performance: 'fast' | 'medium' | 'slow' = 'slow';
    let estimatedLoadTime: number;

    if (hardwareInfo.hasNvidiaGPU || hardwareInfo.hasOllama) {
      performance = 'fast';
      estimatedLoadTime = modelEstimate.fast;
    } else if (hardwareInfo.hasGPU && hardwareInfo.recommendedBackend === 'webgpu') {
      performance = 'medium';
      estimatedLoadTime = modelEstimate.medium;
    } else {
      performance = 'slow';
      estimatedLoadTime = modelEstimate.slow;
    }

    const allModels = await this.getAvailableModels();
    const recommendedModels = hardwareDetector.getRecommendedModels(hardwareInfo, allModels);
    const recommended = recommendedModels.some((m: ModelMetadata) => m.id === modelId);

    return { estimatedLoadTime, recommended, performance };
  }
}

// Hardware detection utility script
export const detectAndLogHardware = async () => {
  console.log('🔍 Detecting hardware capabilities...');

  const hardwareInfo = await hardwareDetector.detectHardware();

  console.log('📊 Hardware Report:');
  console.log(`   GPU Available: ${hardwareInfo.hasGPU ? '✅' : '❌'}`);
  console.log(`   WebGPU Support: ${hardwareInfo.hasWebGPU ? '✅' : '❌'}`);
  console.log(`   WebGL Support: ${hardwareInfo.hasWebGL ? '✅' : '❌'}`);
  console.log(`   NVIDIA GPU: ${hardwareInfo.hasNvidiaGPU ? '✅' : '❌'}`);
  console.log(`   Ollama Available: ${hardwareInfo.hasOllama ? '✅' : '❌'}`);
  console.log(`   Recommended Backend: ${hardwareInfo.recommendedBackend.toUpperCase()}`);
  console.log(`   Mobile Device: ${hardwareInfo.isMobile ? '✅' : '❌'}`);

  if (hardwareInfo.gpuVendor || hardwareInfo.gpuRenderer) {
    console.log(`   GPU Vendor: ${hardwareInfo.gpuVendor || 'Unknown'}`);
    console.log(`   GPU Renderer: ${hardwareInfo.gpuRenderer || 'Unknown'}`);
  }

  const modelManager = new ModelManager();
  const recommendedModels = hardwareDetector.getRecommendedModels(hardwareInfo, await modelManager.getAvailableModels());

  console.log('\n🤖 Recommended Models for your hardware:');
  recommendedModels.forEach(model => {
    console.log(`   • ${model.name} (${model.id})`);
  });

  console.log('\n💡 Performance Tips:');
  if (hardwareInfo.hasNvidiaGPU) {
    console.log('   • NVIDIA GPU detected - you can run larger models efficiently');
    console.log('   • Consider using Ollama for even better GPU utilization');
  } else if (hardwareInfo.hasWebGPU) {
    console.log('   • WebGPU supported - good performance expected');
  } else if (hardwareInfo.hasWebGL) {
    console.log('   • WebGL fallback - moderate performance');
  } else {
    console.log('   • CPU-only mode - use smallest models for best experience');
  }

  return hardwareInfo;
};

export { hardwareDetector };
export default new ModelManager();